/** @format */

import { faEdit, faPlus } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { api } from '../../../api.ts';
import { ThemeProvider } from '../../../theme';
import { parseFieldError } from '../../../utils/errors';

const discountTypeChoices = [
  { value: 'fixed', label: 'Montant fixe' },
  { value: 'percentage', label: 'Pourcentage' },
];

const assignmentTypeChoices = [
  { value: 'business_introducer', label: "Apporteur d'affaire" },
  { value: 'zip_code', label: 'Code postal' },
];

function AdminSpecialDiscountsManager() {
  const { enqueueSnackbar } = useSnackbar();
  const [specialDiscounts, setSpecialDiscounts] = useState([]);
  const [businessIntroducers, setBusinessIntroducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: 'name',
    direction: 'asc',
  });
  const [formData, setFormData] = useState({
    is_active: false,
    assignment_type: 'business_introducer',
    business_introducer: '',
    zip_code: '',
    discount_type: 'fixed',
    discount: '',
    name: '',
    internal_name: '',
    quote_name: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [discountsResponse, introducersResponse] = await Promise.all([
        api.get('/special-discounts/'),
        api.get('/leads-business-introducers/'),
      ]);

      setSpecialDiscounts(discountsResponse?.data?.results || []);
      setBusinessIntroducers(introducersResponse?.data?.results || []);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Erreur lors du chargement des données', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Sorting logic
  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedDiscounts = [...specialDiscounts].sort((a, b) => {
    const { field, direction } = sortConfig;
    let aValue = a[field];
    let bValue = b[field];

    // Handle special cases
    if (field === 'business_introducer') {
      const aIntroducer = businessIntroducers.find((bi) => bi.id === aValue);
      const bIntroducer = businessIntroducers.find((bi) => bi.id === bValue);
      aValue = aIntroducer?.name || '';
      bValue = bIntroducer?.name || '';
    }

    if (field === 'assignment_type') {
      aValue =
        assignmentTypeChoices.find((c) => c.value === aValue)?.label || '';
      bValue =
        assignmentTypeChoices.find((c) => c.value === bValue)?.label || '';
    }

    if (field === 'discount_type') {
      aValue = aValue === 'fixed' ? 'Montant fixe' : 'Pourcentage';
      bValue = bValue === 'fixed' ? 'Montant fixe' : 'Pourcentage';
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      is_active: discount.is_active || false,
      assignment_type: discount.assignment_type || 'business_introducer',
      business_introducer: discount.business_introducer || '',
      zip_code: discount.zip_code || '',
      discount_type: discount.discount_type || 'fixed',
      discount: discount.discount || '',
      name: discount.name || '',
      internal_name: discount.internal_name || '',
      quote_name: discount.quote_name || '',
    });
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingDiscount(null);
    setFormData({
      is_active: true,
      assignment_type: 'business_introducer',
      business_introducer: '',
      zip_code: '',
      discount_type: 'fixed',
      discount: '',
      name: '',
      internal_name: '',
      quote_name: '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = {
        is_active: formData.is_active,
        assignment_type: formData.assignment_type,
        discount_type: formData.discount_type,
        discount: formData.discount ? parseFloat(formData.discount) : 0,
        name: formData.name,
        internal_name: formData.internal_name,
        quote_name: formData.quote_name,
      };
      if (formData.assignment_type === 'zip_code') {
        data.zip_code = (formData.zip_code || '').trim();
        data.business_introducer = null;
      } else {
        data.business_introducer = formData.business_introducer || null;
        data.zip_code = null;
      }

      if (editingDiscount) {
        // Update existing
        await api.patch(`/special-discounts/${editingDiscount.id}/`, {}, data);
        enqueueSnackbar('Remise spéciale mise à jour', { variant: 'success' });
      } else {
        // Create new
        await api.post('/special-discounts/', {}, data);
        enqueueSnackbar('Remise spéciale créée', { variant: 'success' });
      }

      setDialogOpen(false);
      loadData(); // Refresh the list
    } catch (err) {
      console.error(err);
      const { message } = parseFieldError(err);
      enqueueSnackbar(message || 'Erreur lors de la sauvegarde', {
        variant: 'error',
      });
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingDiscount(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          p={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography>Chargement...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <ThemeProvider>
      <Container maxWidth="xl">
        <Box p={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Gestion des remises spéciales
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Remises liées à un apporteur d&apos;affaires ou à un code postal
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleCreate}
              size="large"
            >
              Nouvelle remise
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'is_active'}
                      direction={
                        sortConfig.field === 'is_active'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('is_active')}
                    >
                      Actif
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'assignment_type'}
                      direction={
                        sortConfig.field === 'assignment_type'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('assignment_type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'business_introducer'}
                      direction={
                        sortConfig.field === 'business_introducer'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('business_introducer')}
                    >
                      Apporteur d&apos;affaires
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'zip_code'}
                      direction={
                        sortConfig.field === 'zip_code'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('zip_code')}
                    >
                      Code postal
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'name'}
                      direction={
                        sortConfig.field === 'name'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('name')}
                    >
                      Nom
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'internal_name'}
                      direction={
                        sortConfig.field === 'internal_name'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('internal_name')}
                    >
                      Nom interne
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'discount_type'}
                      direction={
                        sortConfig.field === 'discount_type'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('discount_type')}
                    >
                      Type de remise
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={sortConfig.field === 'discount'}
                      direction={
                        sortConfig.field === 'discount'
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort('discount')}
                    >
                      Montant
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedDiscounts.map((discount) => (
                  <TableRow key={discount.id}>
                    <TableCell>
                      <Checkbox checked={discount.is_active} disabled />
                    </TableCell>
                    <TableCell>
                      {assignmentTypeChoices.find(
                        (c) =>
                          c.value ===
                          (discount.assignment_type || 'business_introducer'),
                      )?.label || "Apporteur d'affaire"}
                    </TableCell>
                    <TableCell>
                      {discount.assignment_type === 'zip_code'
                        ? '—'
                        : businessIntroducers.find(
                            (bi) => bi.id === discount.business_introducer,
                          )?.name || '—'}
                    </TableCell>
                    <TableCell>
                      {discount.assignment_type === 'zip_code'
                        ? discount.zip_code || '—'
                        : '—'}
                    </TableCell>
                    <TableCell>{discount.name}</TableCell>
                    <TableCell>{discount.internal_name}</TableCell>
                    <TableCell>
                      {discount.discount_type === 'fixed'
                        ? 'Montant fixe'
                        : 'Pourcentage'}
                    </TableCell>
                    <TableCell>{discount.discount}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEdit(discount)}
                        size="medium"
                        color="primary"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {specialDiscounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        Aucune remise spéciale trouvée
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Créez votre première remise spéciale pour commencer
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={dialogOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h5" component="h2">
                {editingDiscount
                  ? 'Modifier la remise spéciale'
                  : 'Nouvelle remise spéciale'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={3} mt={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>Actif:</Typography>
                  <Checkbox
                    checked={formData.is_active}
                    onChange={(e) =>
                      handleInputChange('is_active', e.target.checked)
                    }
                  />
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.assignment_type}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        assignment_type: v,
                        ...(v === 'zip_code'
                          ? { business_introducer: '' }
                          : { zip_code: '' }),
                      }));
                    }}
                    label="Type"
                  >
                    {assignmentTypeChoices.map((choice) => (
                      <MenuItem key={choice.value} value={choice.value}>
                        {choice.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {formData.assignment_type === 'business_introducer' ? (
                  <FormControl fullWidth required>
                    <InputLabel>Apporteur d&apos;affaires</InputLabel>
                    <Select
                      value={formData.business_introducer}
                      onChange={(e) =>
                        handleInputChange('business_introducer', e.target.value)
                      }
                      label="Apporteur d'affaires"
                    >
                      {businessIntroducers.map((bi) => (
                        <MenuItem key={bi.id} value={bi.id}>
                          {bi.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Code postal"
                    value={formData.zip_code}
                    onChange={(e) =>
                      handleInputChange(
                        'zip_code',
                        e.target.value.replace(/\D/g, '').slice(0, 5),
                      )
                    }
                    fullWidth
                    required
                    inputProps={{ maxLength: 5 }}
                    helperText="5 chiffres"
                  />
                )}

                <FormControl fullWidth>
                  <InputLabel>Type de remise</InputLabel>
                  <Select
                    value={formData.discount_type}
                    onChange={(e) =>
                      handleInputChange('discount_type', e.target.value)
                    }
                    label="Type de remise"
                  >
                    {discountTypeChoices.map((choice) => (
                      <MenuItem key={choice.value} value={choice.value}>
                        {choice.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Montant"
                  type="number"
                  value={formData.discount}
                  onChange={(e) =>
                    handleInputChange('discount', e.target.value)
                  }
                  fullWidth
                />

                <TextField
                  label="Nom (factures)"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  fullWidth
                  required
                />

                <TextField
                  label="Nom (devis)"
                  value={formData.quote_name}
                  onChange={(e) =>
                    handleInputChange('quote_name', e.target.value)
                  }
                  fullWidth
                  required
                  helperText="Max. 35 caractères"
                  inputProps={{ maxLength: 35 }}
                />

                <TextField
                  label="Nom interne"
                  value={formData.internal_name}
                  onChange={(e) =>
                    handleInputChange('internal_name', e.target.value)
                  }
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
              <Button onClick={handleClose} size="large">
                Annuler
              </Button>
              <Button onClick={handleSave} variant="contained" size="large">
                Sauvegarder
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default AdminSpecialDiscountsManager;
